/**
 * AuthStore - Zustand Store for Authentication State
 *
 * This store manages authentication state using Zustand for lightweight,
 * unopinionated state management. It integrates with AuthService for
 * authentication operations.
 */

import { create } from 'zustand';
import {
  LogoutOptions,
  LogoutReason,
  Permission,
  User,
  UserProfile,
  UserRole,
} from '../../../shared/auth';
import {
  setUserProperties,
  trackEvent,
  Permission as AnalyticsPermission,
} from '@peritasai/analytics';
import { AuthService } from '../services/auth.service';
import {
  getOrCreateTabId,
  openInactivityChannel,
  publishSharedForceLogout,
} from '../inactivity/crossTab';

function toAnalyticsUser(user: User) {
  return {
    id: user.id,
    email: user.username, // Zitadel username is the user's email — SHA256 hashed in setUserProperties
    permissions: user.isAdmin
      ? [AnalyticsPermission.ADMIN]
      : user.permissions.filter(
          (p): p is AnalyticsPermission =>
            typeof p === 'string' &&
            (Object.values(AnalyticsPermission) as string[]).includes(p)
        ),
    sessionKey: user.sessionKey,
  };
}

/**
 * Authentication store state interface
 */
interface AuthState {
  // User data
  user: User | null;

  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  login: () => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
  handlePKCECallback: (
    code: string,
    codeVerifier: string,
    redirectUri: string
  ) => Promise<void>;
  clearError: () => void;
  _setError: (error: string) => void;
  handleRedirectFailure: () => void;

  // Utility methods
  hasPermission: (permission: Permission) => boolean;
  isAdmin: () => boolean;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  getUserProfile: (userId: string) => Promise<UserProfile | null>;
  updateUserProfile: (
    userId: string,
    updates: Partial<UserProfile>
  ) => Promise<UserProfile | null>;
}

/**
 * Create the authentication store
 */
export const useAuthStore = create<AuthState>((set, get) => {
  const { promise: initialized, resolve: resolveInitialized } =
    Promise.withResolvers<void>();

  return {
  // Initial state
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  /**
   * Initialize authentication state
   */
  initialize: async () => {
    if (get().isLoading || get().isInitialized) return initialized;

    try {
      set({ isLoading: true, error: null });

      await AuthService.initialize();

      const currentUser = AuthService.getCurrentUser();

      if (currentUser) {
        set({
          user: currentUser,
          isAuthenticated: true,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
        setUserProperties(toAnalyticsUser(currentUser));
      } else {
        set({
          isAuthenticated: false,
          user: null,
          isLoading: false,
          isInitialized: true,
          error: null,
        });
      }
    } catch (error) {
      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        isInitialized: true,
        error: error instanceof Error ? error.message : 'Initialization failed',
      });
    } finally {
      resolveInitialized();
    }

    return initialized;
  },

  /**
   * Login user
   */
  login: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await AuthService.login();

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      setUserProperties(toAnalyticsUser(response.user));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Login failed';
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });

      throw error;
    }
  },

  /**
   * Handle PKCE callback
   */
  handlePKCECallback: async (
    code: string,
    codeVerifier: string,
    redirectUri: string
  ) => {
    try {
      set({ isLoading: true, error: null });

      const response = await AuthService.handlePKCECallback(
        code,
        codeVerifier,
        redirectUri
      );

      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      setUserProperties(toAnalyticsUser(response.user));
      trackEvent('login', { method: 'pkce' });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'PKCE callback failed';
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: errorMessage,
      });
      trackEvent('login_error', { error_message: errorMessage });
      throw error;
    }
  },

  /**
   * Logout user.
   * When redirecting to Zitadel end_session, skips state update to prevent login() from cancelling the navigation.
   */
  logout: async (options = {}) => {
    const clearLocalAuthState = () => {
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    };
    const redirectToLogin = () => {
      if (typeof window !== 'undefined') {
        window.location.assign('/');
      }
    };

    try {
      const { user } = get();
      if (!user) {
        clearLocalAuthState();
        return;
      }

      const reason = options.reason ?? LogoutReason.MANUAL;
      trackEvent('logout', { reason });
      setUserProperties(null);
      const shouldBroadcast =
        options.broadcast ?? reason === LogoutReason.MANUAL;
      let redirected = false;

      set({ isLoading: true });

      if (shouldBroadcast && user.sessionKey) {
        const channel = openInactivityChannel();
        try {
          publishSharedForceLogout(
            user.sessionKey,
            {
              type: 'forceLogout',
              sessionKey: user.sessionKey,
              userId: user.id,
              timestamp: Date.now(),
              reason,
              sourceTabId: getOrCreateTabId(),
            },
            channel
          );
        } finally {
          channel?.close();
        }
      }

      const result = await AuthService.logout();
      redirected = Boolean(result?.redirected);
      if (redirected) {
        return;
      }
    } catch {
      clearLocalAuthState();
      return;
    }

    clearLocalAuthState();

    if (
      options.reason === LogoutReason.TIMEOUT ||
      options.reason === LogoutReason.REMOTE
    ) {
      redirectToLogin();
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Called when logout redirect to Zitadel fails (timeout). Clears store state.
   */
  handleRedirectFailure: () => {
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  },

  /**
   * Set error state (internal use)
   */
  _setError: (error: string) => {
    set({ error });
  },

  /**
   * Check if user has specific permission (or is admin)
   */
  hasPermission: (permission: Permission) => {
    const { user } = get();
    if (!user) return false;
    if (user.isAdmin) return true;
    return user.permissions.includes(permission);
  },

  /**
   * Check if user has admin role
   */
  isAdmin: () => {
    const { user } = get();
    return user?.isAdmin ?? false;
  },

  /**
   * Check if user has specific role (ADMIN = isAdmin, others = permission)
   */
  hasRole: (role: UserRole) => {
    const { user } = get();
    if (!user) return false;
    if (role === UserRole.ADMIN) return user.isAdmin;
    return user.permissions.includes(role as unknown as Permission);
  },

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole: (roles: UserRole[]) => {
    const { user } = get();
    if (!user) return false;
    if (roles.includes(UserRole.ADMIN) && user.isAdmin) return true;
    return roles.some((r) =>
      r === UserRole.ADMIN
        ? user.isAdmin
        : user.permissions.includes(r as unknown as Permission)
    );
  },

  /**
   * Get user profile
   */
  getUserProfile: async (userId: string) => {
    return await AuthService.getUserProfile(userId);
  },

  /**
   * Update user profile
   */
  updateUserProfile: async (userId: string, updates: Partial<UserProfile>) => {
    return await AuthService.updateUserProfile(userId, updates);
  },
  }; // end return
});

// Selector functions for easier state access
export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) =>
  state.isAuthenticated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
export const selectIsInitialized = (state: AuthState) => state.isInitialized;
